import type { GraphQLTaggedNode } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/writeFragmentInterop.ts';

import type {
	EditorContentFragmentRefKey,
	EditorContentFragmentValue,
} from './useEditorContentFragment.ts';

type PartialFragmentValue<T> =
	T extends ReadonlyArray<infer TItem>
		? ReadonlyArray<PartialFragmentValue<TItem>>
		: T extends object
			? {
					readonly [K in keyof T as K extends ' $fragmentType' | ' $fragmentSpreads'
						? never
						: K]?: PartialFragmentValue<T[K]>;
				}
			: T;

/** The fields of a @catch fragment to write, any subset of its value. */
export type EditorContentFragmentWriteData<TKey extends EditorContentFragmentRefKey> =
	PartialFragmentValue<EditorContentFragmentValue<TKey>>;

/** The document whose record is written, and the fragment argument the container passes. */
export type EditorContentFragmentWriteArgs = {
	contentId: string;
	isLivePage: boolean;
};

/**
 * Writes fields of a fragment of the editor document onto the page's record, the record the
 * fragment reads, whatever root arguments the query that fetched it had. Only the given fields
 * change. Inside a mutation updater it writes onto the optimistic layer, so it is reverted with it.
 */
export function writeEditorContentFragment<TKey extends EditorContentFragmentRefKey>(
	fragmentNode: GraphQLTaggedNode,
	{ contentId, isLivePage }: EditorContentFragmentWriteArgs,
	data: EditorContentFragmentWriteData<TKey>,
): void {
	writeFragmentInterop({
		fragmentId: `Content:${contentId}`,
		fragment: fragmentNode,
		data,
		variables: { isLivePage },
	});
}
